import React from 'react';
import { toast } from 'react-toastify';
import { useMatch } from 'react-router-dom';
import { useRecording } from '@src/features/order/hooks/useRecording';
import { useGlobalCamera } from '@src/core/providers/global-camera-provider';
import { ReloadIcon, CollapseIcon } from './icons';
import { ArcCircularProgress, ArcIconButton, ArcStack, ArcText, ArcTooltip } from '@src/core/ui';
import { routesConfig } from '@src/core/routes';
import { ToolbarItem } from './types';
import CameraToolbar from './camera-toolbar';
import RecordingIndicator from './recording-indicator';
import CameraActions from '@src/features/order/components/camera-actions';
import GoHomeAction from '@src/features/order/components/go-home-action';
import WinchDownAction from '@src/features/order/components/winch-down-action';
import VideoQualitySelector from './video-quality-selector';
import VideoModeSelector from './video-mode-selector';
import CameraPortalTarget from './camera-portal-target';
import {
  cameraFullscreenPanelSx,
  cameraSplitPanelSx,
  pipCollapsedExpandButtonSx,
  pipOverlaySx,
} from './pip-overlay-sx';

/**
 * CameraView renders the UI shell around the persistent global video
 * element (toolbar, recording indicator, loading overlay, action buttons).
 *
 * The <video> DOM node itself lives in GlobalCameraProvider and is moved
 * into this view via CameraPortalTarget using DOM appendChild, preserving
 * the WebRTC MediaStream across page navigation.
 */
const CameraView: React.FC = () => {
  const {
    activeSource,
    isLoading,
    isReconnecting,
    reconnect,
    viewMode,
    setViewMode,
    collapsed,
    toggleCollapse,
  } = useGlobalCamera();

  const isOverlay = viewMode === 'default';
  const showToolbar = true;
  // En-route Go Home / Winch Down actions live in the drones-page sidebar
  // (SelectedDrone card) per design — hide the camera overlay version on
  // `/app/drones` to avoid duplicate controls. They remain visible on the
  // order take-off-to-en-route flow which has no equivalent sidebar button.
  const isDronesRoute = !!useMatch(`${routesConfig.drones}/*`);

  const serialNumber = activeSource?.serialNumber ?? '';
  const isViewer = activeSource?.isViewer ?? false;
  const isEnRoute = activeSource?.isEnRoute ?? false;
  const resetGoHere = activeSource?.resetGoHere;

  // Camera controls (zoom / gimbal) are shown whenever the camera is
  // expanded beyond the small map overlay — i.e. in `split` or `pip`
  // (Swap / fullscreen) modes. Viewers don't get write commands.
  const showCameraControls = !isOverlay && !isViewer && !!serialNumber;

  const { isRecording } = useRecording(serialNumber);

  const loading = isLoading || isReconnecting;

  // Grace period after a reconnect finishes: the WebRTC connection
  // reports "connected" before the first decoded video frame actually
  // paints, so the <video> element shows a black screen for a few
  // seconds. Keep the Reload button disabled during this window so the
  // operator doesn't spam-click reconnect while the stream is warming
  // up. See PM bug report.
  const RECONNECT_COOLDOWN_MS = 5000;
  const [reconnectCooldown, setReconnectCooldown] = React.useState(false);
  const prevReconnectingRef = React.useRef(isReconnecting);
  React.useEffect(() => {
    const wasReconnecting = prevReconnectingRef.current;
    prevReconnectingRef.current = isReconnecting;
    if (wasReconnecting && !isReconnecting) {
      setReconnectCooldown(true);
      const t = setTimeout(() => setReconnectCooldown(false), RECONNECT_COOLDOWN_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isReconnecting]);

  const toolbarItems = React.useMemo<ToolbarItem[]>(() => {
    const items: ToolbarItem[] = [
      {
        type: 'button',
        key: 'Reconnect',
        icon: <ReloadIcon />,
        onClick: reconnect,
        disabled: isReconnecting || reconnectCooldown,
      },
      {
        type: 'slot',
        key: 'video-quality',
        node: <VideoQualitySelector serialNumber={serialNumber} />,
      },
      {
        type: 'slot',
        key: 'video-mode',
        node: <VideoModeSelector />,
      },
    ];

    // Default only: small video overlay collapse. Swap uses map PiP control; split has no collapse.
    if (viewMode === 'default') {
      items.push({
        type: 'button',
        key: collapsed ? 'Expand' : 'Collapse',
        icon: <CollapseIcon />,
        onClick: toggleCollapse,
      });
    }

    return items;
  }, [
    collapsed,
    isReconnecting,
    reconnect,
    reconnectCooldown,
    serialNumber,
    toggleCollapse,
    viewMode,
  ]);

  const isCollapsedOverlay = isOverlay && collapsed;

  if (!activeSource) return null;

  return (
    <>
      {isCollapsedOverlay && (
        <ArcTooltip title="Expand">
          <ArcIconButton
            onClick={toggleCollapse}
            sx={pipCollapsedExpandButtonSx}>
            <CollapseIcon sx={{ transform: 'rotate(180deg)' }} />
          </ArcIconButton>
        </ArcTooltip>
      )}

      <ArcStack
        p={isOverlay ? 0.5 : 0}
        zIndex={isOverlay ? 100 : undefined}
        display={isCollapsedOverlay ? 'none' : 'flex'}
        sx={(theme) => {
          if (isOverlay) return pipOverlaySx(theme);
          if (viewMode === 'split') return cameraSplitPanelSx(theme);
          return cameraFullscreenPanelSx(theme);
        }}>

        <RecordingIndicator
          isRecording={isRecording}
          onClick={() => toast.error('Recording is not available yet')}
        />

        {showToolbar && (
          <CameraToolbar items={toolbarItems} />
        )}

        {showCameraControls && <CameraActions serialNumber={serialNumber} />}

        {loading && (
          <ArcStack
            spacing={1}
            width='100%'
            height='100%'
            position='absolute'
            top={0}
            left={0}
            alignItems='center'
            justifyContent='center'>
            <ArcCircularProgress color='ghost' />
            <ArcStack>
              <ArcText.h2 textAlign='center'> Connecting... </ArcText.h2>
              <ArcText.caption textAlign='center'> Please wait </ArcText.caption>
            </ArcStack>
          </ArcStack>
        )}

        <ArcStack
          width='100%'
          height='100%'
          overflow='hidden'
          alignItems='center'
          justifyContent='center'>
          <CameraPortalTarget />
        </ArcStack>

        {(isEnRoute && !isViewer && !isDronesRoute) && (
          <ArcStack
            p={1}
            left={10}
            right={10}
            bottom={20}
            spacing={1}
            direction='row'
            position='absolute'
            justifyContent='center'>
            <GoHomeAction serialNumber={serialNumber} resetGoHere={resetGoHere ?? (() => {})} />
            <WinchDownAction serialNumber={serialNumber} />
          </ArcStack>
        )}
      </ArcStack>
    </>
  );
};

export default CameraView;
